import { isWithinOneMonth } from '@/components/orders/utils/PharmacistOrdersTableUtils';
import settings from '@/constants/constants';
import { FF_PHARMACIST_ORDER_LISTS_DATE_RANGE_FILTER } from '@/constants/featureFlags';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { useDownload } from '@montugroup/circuit-shared';
import { Badge, Button, toast } from '@montugroup/design-system';
import Download from '@mui/icons-material/Download';
import { DateTime } from 'luxon';

type Props = {
  selectedOrders: number[];
  count: number;
  isLoading: boolean;
  startDate: Date | null;
  endDate: Date | null;
  search: string;
  statusFilter: number[];
};

function DownloadOrdersButton({ selectedOrders, count, startDate, endDate, isLoading, search, statusFilter }: Props) {
  const { flags } = useFeatureFlags();
  const { isDownloading, downloadFile } = useDownload();

  const generateExport = async () => {
    let startDateLuxon = null;
    let endDateLuxon = null;
    let fileName = `${Date.now()}-pharmacy-orders`;

    if (flags[FF_PHARMACIST_ORDER_LISTS_DATE_RANGE_FILTER]) {
      if (selectedOrders.length === 0 && count === 0) {
        toast.error('Filters must return orders before proceeding with action.');
        return;
      }

      if (!startDate || !isWithinOneMonth({ startDate, endDate: endDate || new Date() })) {
        toast.error('Outside date range limit. Maximum date range is 1 month.');
        return;
      }

      startDateLuxon = DateTime.fromJSDate(startDate);
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      endDateLuxon = DateTime.fromJSDate(endDate || new Date());

      // Example fileName: INVOICE_06-JAN-2025_TO_05-FEB-2025
      fileName = `INVOICE_${startDateLuxon.toFormat('dd-LLL-yyyy').toUpperCase()}_TO_${endDateLuxon.toFormat('dd-LLL-yyyy').toUpperCase()}`;
    }

    await downloadFile(
      fileName,
      `${settings.url}/excel/pharmacist/exportOrders`,
      {
        method: 'POST',
        responseType: 'blob',
        params: {
          sort: 'dispensed_date',
          sortOrder: 'DESC',
          filter: search,
          ...(selectedOrders.length && { orderIds: selectedOrders }),
          statusFilter,
          ...(startDate && { startDate: DateTime.fromJSDate(startDate).toJSON() }),
          ...(endDateLuxon && { endDate: endDateLuxon.endOf('day').toJSON() }),
        },
      },
      '.xlsx',
    );
  };

  return (
    <Badge
      badgeContent={selectedOrders.length || count}
      color={selectedOrders.length === 0 && count === 0 ? 'error' : 'secondary'}
      showZero
      sx={{ width: { sm: '100%', md: 'auto' } }}
    >
      <Button
        variant="contained"
        size="large"
        sx={{ width: { sm: '100%', md: 'auto' } }}
        onClick={generateExport}
        disabled={isDownloading || isLoading}
        startIcon={<Download />}
      >
        Download orders
      </Button>
    </Badge>
  );
}

export default DownloadOrdersButton;
